import SortIcon from '@mui/icons-material/Sort';
import TuneIcon from '@mui/icons-material/Tune';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from 'atoms/Button';
import Container from 'atoms/Container';
import Select, { SelectOption } from 'atoms/Select';
import cn from 'classnames';
import { burgerMenuItems } from 'config/navigation';
import { observer } from 'mobx-react-lite';
import BreadcrumbsHeader from 'molecules/BreadcrumbsHeader/BreadcrumbsHeader';
import SwitchMapViewButton from 'molecules/SwitchView/SwitchMapViewButton';
import SwitchTableViewBar from 'molecules/SwitchView/SwitchTableViewBar';
import TimeZoneSelect from 'molecules/TimeZoneSelect';
import HeaderSearch from 'organisms/BrowsingHeader/HeaderSearch';
import FiltersSidebar from 'organisms/FiltersSidebar';
import SortingSidebar from 'organisms/SortingSidebar';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ITableSortBy } from 'stores/interfaces';
import { useStore } from 'stores/store';
import { IAggRequest, IFilter, IFilterOptionsItem } from 'utils/globalTypes';
import { useClickOutside } from 'utils/hooks/useClickOutside';
import { useMobileDetect } from 'utils/hooks/useMobileDetect';
import { ISearch } from 'utils/networkRequests';

import s from './BrowsingHeader.module.scss';

interface BrowsingHeaderData {
  requestCmd?: string;
  showSwitchBar?: boolean;
  showTimzeZoneSelect?: boolean;
  mapView?: boolean;
  showTableView?: boolean;
  showBreadcrumbs?: boolean;
  showOfferingSearch?: boolean;
  showUserSearch?: boolean;
  showFilters?: boolean;
  showFilterCounts?: boolean;
  fullWidth?: boolean;
  total?: number;
  entityName?: string;
  children?: ReactNode;
  defaultSearchTerm?: string;
  onSelect?: (search?: ISearch) => void;
  onSearch?: (search?: ISearch) => void;
  setFilters?: (filters?: IFilter[]) => void;
  setSortField?: (key: string) => void;
  aggregations?: IAggRequest;
  filterOptions?: IFilterOptionsItem[];
  sortOptions?: SelectOption[];
  sortOrder?: ITableSortBy;
}

const BrowsingHeader = ({
  requestCmd,
  showSwitchBar,
  showTimzeZoneSelect,
  mapView,
  showTableView = true,
  showBreadcrumbs = true,
  showOfferingSearch = true,
  showUserSearch = false,
  showFilters = true,
  showFilterCounts = true,
  fullWidth = true,
  total,
  defaultSearchTerm,
  children,
  onSelect,
  onSearch,
  setFilters,
  setSortField,
  aggregations,
  filterOptions,
  entityName,
  sortOptions,
  sortOrder
}: BrowsingHeaderData) => {
  const store = useStore();
  const mainFilterRef = useRef<HTMLDivElement>(null);
  const moreFilterRef = useRef<HTMLDivElement>(null);
  const { isMobile, isTablet } = useMobileDetect();
  const [isSidebarShown, setIsSidebarShown] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [selectedSorting, setSelectedSorting] = useState('');
  const [selectedFilterCount, setSelectedFilterCount] = useState(0);

  useEffect(() => {
    if (aggregations) {
      store.MarketplaceStore.runStatsQuery(filterOptions, aggregations);
    } else {
      store.MarketplaceStore.setFilterOptions(filterOptions);
    }
  }, []);

  const onClearAll = useCallback(() => {
    store.MarketplaceStore.clearFilters();
    setFilters && setFilters();
    if (onSearch) {
      onSearch();
    } else {
      store.MarketplaceStore.runStatsQuery(filterOptions, aggregations);
    }
  }, []);

  useClickOutside({
    refs: [mainFilterRef, moreFilterRef],
    callback: () => {
      if (mainFilterRef.current && mainFilterRef.current.classList.contains('isActive')) {
        mainFilterRef.current.classList.remove('isActive');
      }
    }
  });

  return (
    <div className={cn(s.BrowsingHeader, { mapView })}>
      <Container narrowPadding={fullWidth} fullWidth={fullWidth} withoutPadding={!fullWidth}>
        {showBreadcrumbs && (
          <BreadcrumbsHeader navItems={burgerMenuItems} className={s.BrowsingHeader__desktopHide} />
        )}
        {(showOfferingSearch || showUserSearch || showFilters || showSwitchBar) && (
          <div className={s.BrowsingHeader__row}>
            {children}
            {showSwitchBar && !isTablet && !isMobile && (
              <SwitchTableViewBar showTableView={showTableView} />
            )}
            <div className={s.BrowsingHeader__controls}>
              {showSwitchBar && (isMobile || isTablet) && <SwitchMapViewButton />}
              {(showOfferingSearch || showUserSearch) && (
                <HeaderSearch
                  onSelect={onSelect}
                  onSearch={onSearch}
                  requestCmd={requestCmd}
                  showUserSearch={showUserSearch}
                  showOfferingSearch={showOfferingSearch}
                  defaultSearchTerm={defaultSearchTerm}
                />
              )}
              {showTimzeZoneSelect && <TimeZoneSelect className={s.BrowsingHeader__mobileHide} />}
              {!isMobile && sortOptions && setSortField && (
                <Select
                  size="small"
                  defaultValue={sortOrder?.id || ''}
                  sx={{ width: '190px' }}
                  displayEmpty
                  options={sortOptions}
                  renderValue={(selected) => {
                    const showVal = (val: string) => (
                      <Box component="span" sx={{ fontSize: 14 }}>
                        {val}
                      </Box>
                    );
                    if (!selected) return showVal('Sort By');

                    return showVal(sortOptions.find(({ value }) => value === selected)?.name ?? '');
                  }}
                  onChange={(e) => {
                    setSelectedSorting(e.target.value as string);
                    setSortField(e.target.value as string);
                  }}
                  aria-label="Sort"
                />
              )}
              {isMobile && sortOptions && setSortField && (
                <Button
                  size="small"
                  className={s.BrowsingHeader__filters}
                  onClick={() => setIsFiltering(true)}
                  endIcon={<SortIcon />}
                  variant="outlined"
                  aria-label="Filter"
                />
              )}
              {showFilters && (
                <Badge
                  badgeContent={selectedFilterCount || 0}
                  classes={{ badge: s.BrowsingHeader__badge }}
                >
                  <Button
                    size="small"
                    className={cn(s.BrowsingHeader__filters, {
                      isHighlighted: selectedFilterCount > 0
                    })}
                    onClick={() => setIsSidebarShown(!isSidebarShown)}
                    endIcon={<TuneIcon />}
                    variant="outlined"
                    aria-label="Filter"
                  >
                    <span className={s.BrowsingHeader__mobileHide}>{'Filters'}</span>
                  </Button>
                </Badge>
              )}
            </div>
          </div>
        )}
        {showFilters && isSidebarShown && (
          <FiltersSidebar
            isMobile={isMobile}
            isOpen={isSidebarShown}
            onClose={() => setIsSidebarShown(false)}
            filterOptions={store.MarketplaceStore.getFilterOptions}
            showCounts={showFilterCounts}
            setFilters={setFilters}
            onClearAll={onClearAll}
            filters={store.MarketplaceStore.getFilters}
            onSelectionUpdate={setSelectedFilterCount}
            entityName={entityName}
            total={total}
          />
        )}
        {sortOptions && isFiltering && (
          <SortingSidebar
            isMobile={isMobile}
            activeOption={selectedSorting}
            isOpen={isFiltering}
            onClose={() => setIsFiltering(false)}
            sortOptions={sortOptions}
            onSelect={(val) => {
              setSelectedSorting(val);
              setSortField && setSortField(val);
            }}
          />
        )}
      </Container>
    </div>
  );
};

export default observer(BrowsingHeader);
